import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/terrapin-logo.png';

// NavLink component
const NavLink = ({ to, children, mobile, onNavigate }) => {
  const location = useLocation();
  const isActive = location.pathname === to || 
    (location.pathname === '/' && to === '/') ||
    (location.pathname !== '/' && to !== '/' && location.pathname.startsWith(to));

  const handleClick = () => {
    if (mobile && onNavigate) {
      onNavigate();
    }
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      className={`
        ${mobile ? 'block w-full text-lg' : 'text-base'} 
        px-4 py-2 rounded-md transition-colors duration-200 whitespace-nowrap
        ${isActive
          ? 'bg-terrapin-red text-white'
          : 'text-gray-700 hover:text-terrapin-blue'
        }
      `}
    >
      {children}
    </Link>
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const handleNavigate = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <nav className="sticky top-0 bg-white shadow-lg z-50">
        {/* Main navbar */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center justify-between w-full">
              {/* Logo - always visible */}
              <Link to="/" className="flex-shrink-0 flex items-center">
                <img 
                  src={logo} 
                  alt="Terrapin Networks Logo" 
                  className="h-8 w-auto sm:h-10"
                />
              </Link>

              {/* Mobile menu button */}
              <button 
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-terrapin-blue focus:outline-none"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                <svg 
                  className="h-6 w-6" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d={isOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                  />
                </svg>
              </button>

              {/* Desktop navigation */}
              <div className="hidden lg:flex items-center space-x-1 xl:space-x-2">
                <NavLink to="/">Home</NavLink>
                <NavLink to="/services">Services</NavLink>
                <NavLink to="/help-desk">Help Desk</NavLink>
                <NavLink to="/our-story">Our Story</NavLink>
                <NavLink to="/our-team">Team</NavLink>
                <NavLink to="/media">Media</NavLink>
                <NavLink to="/contact">Contact Us</NavLink>
                
                <a 
                  href="tel:231-941-2100" 
                  className="btn-base btn-secondary ml-2 xl:ml-4 whitespace-nowrap"
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  231-941-2100
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile navigation sidebar */}
        <div 
          className={`
            fixed top-0 left-0 w-72 h-full bg-white transform transition-transform duration-300 ease-in-out z-50
            ${isOpen ? 'translate-x-0' : '-translate-x-full'}
            lg:hidden
          `}
        >
          <div className="flex flex-col h-full">
            {/* Mobile menu header */}
            <div className="flex items-center justify-between p-4 border-b">
              <img src={logo} alt="Terrapin Networks Logo" className="h-8 w-auto" />
              <button 
                onClick={() => setIsOpen(false)}
                className="rounded-md text-gray-700 hover:text-terrapin-blue focus:outline-none"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Mobile menu links */}
            <div className="flex-1 px-2 py-4 space-y-2 overflow-y-auto">
              <NavLink to="/" mobile onNavigate={handleNavigate}>Home</NavLink>
              <NavLink to="/services" mobile onNavigate={handleNavigate}>Services</NavLink>
              <NavLink to="/help-desk" mobile onNavigate={handleNavigate}>Help Desk</NavLink>
              <NavLink to="/our-story" mobile onNavigate={handleNavigate}>Our Story</NavLink>
              <NavLink to="/our-team" mobile onNavigate={handleNavigate}>Our Team</NavLink>
              <NavLink to="/media" mobile onNavigate={handleNavigate}>Media</NavLink>
              <NavLink to="/contact" mobile onNavigate={handleNavigate}>Contact Us</NavLink>
            </div>

            {/* Mobile menu footer */}
            <div className="p-4 border-t">
              <a 
                href="tel:231-941-2100" 
                className="btn-base btn-secondary w-full justify-center text-lg"
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                231-941-2100
              </a>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
